import { useCallback, useId, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Small } from 'foundations-components/transitional/components/Small';
import { Button } from 'foundations-components/transitional/components/Button';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';

// Internal component — not a public module. Used by ToolBlockRenderer.tsx
// for display-only rendering and by ToolBlockRenderer.stories.tsx for interactive stories.
// Consumers should use AskQuestionSectionFormatter from ai-rendering-library instead.
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function isSingleProps(props) {
  return 'question' in props && !('questions' in props);
}
export function AskUserQuestion(props) {
  if (isSingleProps(props)) {
    if (props.interactive === true) {
      return /*#__PURE__*/_jsx(SingleQuestionWrapper, {
        question: props.question,
        onSubmit: props.onSubmit
      });
    }
    return /*#__PURE__*/_jsx(DisplayOnlyQuestion, {
      question: props.question,
      isPending: props.isPending,
      submittedResponse: props.submittedResponse
    });
  }
  const {
    questions
  } = props;
  if (props.interactive === true) {
    if (questions.length === 0) {
      return /*#__PURE__*/_jsx(_Fragment, {});
    }
    return /*#__PURE__*/_jsx(MultiQuestionStepper, {
      questions: questions,
      onSubmit: props.onSubmit
    });
  }
  return /*#__PURE__*/_jsx(DisplayOnlyQuestions, {
    questions: questions,
    isPending: props.isPending,
    submittedResponses: props.submittedResponses
  });
}
function MultiQuestionStepper({
  questions,
  onSubmit
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [allResponses, setAllResponses] = useState(() => questions.map(() => ({
    selectedOptions: []
  })));
  const [submitted, setSubmitted] = useState(false);
  const handleQuestionSubmit = useCallback(response => {
    const updated = allResponses.map((existing, index) => index === currentIndex ? response : existing);
    setAllResponses(updated);
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setSubmitted(true);
      onSubmit(updated);
    }
  }, [currentIndex, questions.length, allResponses, onSubmit]);
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);
  if (submitted) {
    return /*#__PURE__*/_jsx(DisplayOnlyQuestions, {
      questions: questions,
      isPending: false,
      submittedResponses: allResponses
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "300",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "detail-100",
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "breezeEngineChatComponents.askUserQuestion.questionProgress",
        options: {
          current: currentIndex + 1,
          total: questions.length
        }
      })
    }), /*#__PURE__*/_jsx(SingleInteractiveQuestion, {
      question: questions[currentIndex],
      onSubmit: handleQuestionSubmit,
      submitLabel: currentIndex < questions.length - 1 ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "breezeEngineChatComponents.askUserQuestion.next"
      }) : undefined
    }, currentIndex), currentIndex > 0 && /*#__PURE__*/_jsx(Flex, {
      children: /*#__PURE__*/_jsx(Button, {
        use: "tertiary-light",
        onClick: handlePrevious,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "breezeEngineChatComponents.askUserQuestion.previous"
        })
      })
    })]
  });
}
function SingleQuestionWrapper({
  question,
  onSubmit
}) {
  const [submittedResponse, setSubmittedResponse] = useState(null);
  if (submittedResponse) {
    return /*#__PURE__*/_jsx(DisplayOnlyQuestion, {
      question: question,
      isPending: false,
      submittedResponse: submittedResponse
    });
  }
  return /*#__PURE__*/_jsx(SingleInteractiveQuestion, {
    question: question,
    onSubmit: response => {
      setSubmittedResponse(response);
      onSubmit(response);
    }
  });
}
function SingleInteractiveQuestion({
  question,
  onSubmit,
  submitLabel
}) {
  const {
    question: questionText,
    possibleResponses,
    allowMultipleSelections
  } = question;
  const instanceId = useId();
  const [selectedOptions, setSelectedOptions] = useState(new Set());
  const [freeText, setFreeText] = useState('');
  const canSubmit = selectedOptions.size > 0 || freeText.trim() !== '';
  const handleRadioChange = option => {
    setSelectedOptions(new Set([option]));
    setFreeText('');
  };
  const handleCheckboxChange = (option, checked) => {
    setSelectedOptions(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(option);
      } else {
        next.delete(option);
      }
      return next;
    });
  };
  const handleFreeTextChange = event => {
    const value = event.target.value;
    setFreeText(value);
    if (!allowMultipleSelections && selectedOptions.size > 0) {
      setSelectedOptions(new Set());
    }
  };
  const handleSubmit = () => {
    if (!canSubmit) return;
    const response = {
      selectedOptions: [...selectedOptions]
    };
    if (freeText.trim()) {
      response.freeText = freeText.trim();
    }
    onSubmit(response);
  };
  const freeTextId = `${instanceId}-free-text`;
  const radioName = `${instanceId}-radio`;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "300",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "body-125",
      children: questionText
    }), possibleResponses.length > 0 && /*#__PURE__*/_jsx(ToggleGroup, {
      name: radioName,
      "aria-label": questionText,
      children: allowMultipleSelections ? possibleResponses.map(option => /*#__PURE__*/_jsx(Checkbox, {
        checked: selectedOptions.has(option),
        onChange: ({
          target
        }) => handleCheckboxChange(option, target.checked),
        children: option
      }, option)) : possibleResponses.map(option => /*#__PURE__*/_jsx(RadioInput, {
        checked: selectedOptions.has(option),
        onChange: () => handleRadioChange(option),
        children: option
      }, option))
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "100",
      children: [possibleResponses.length > 0 && /*#__PURE__*/_jsx(Text, {
        as: "label",
        htmlFor: freeTextId,
        typography: "detail-100",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "breezeEngineChatComponents.askUserQuestion.freeTextLabel"
        })
      }), /*#__PURE__*/_jsx(TextArea, {
        id: freeTextId,
        value: freeText,
        onChange: handleFreeTextChange,
        "aria-label": unescapedText('breezeEngineChatComponents.askUserQuestion.freeTextPlaceholder'),
        placeholder: unescapedText('breezeEngineChatComponents.askUserQuestion.freeTextPlaceholder'),
        rows: 3,
        resize: "vertical"
      })]
    }), /*#__PURE__*/_jsx(Flex, {
      justifyContent: "end",
      children: /*#__PURE__*/_jsx(Button, {
        use: "primary",
        disabled: !canSubmit,
        onClick: handleSubmit,
        children: submitLabel !== null && submitLabel !== void 0 ? submitLabel : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "breezeEngineChatComponents.askUserQuestion.submit"
        })
      })
    })]
  });
}
function DisplayOnlyQuestions({
  questions,
  isPending,
  submittedResponses
}) {
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    gap: "300",
    children: questions.map((questionItem, index) => {
      const response = submittedResponses === null || submittedResponses === void 0 ? void 0 : submittedResponses[index];
      return /*#__PURE__*/_jsx(DisplayOnlyQuestion, {
        question: questionItem,
        isPending: isPending,
        submittedResponse: response
      }, index);
    })
  });
}
function DisplayOnlyQuestion({
  question,
  isPending,
  submittedResponse
}) {
  const {
    question: questionText
  } = question;
  const responseTexts = [];
  if (submittedResponse) {
    responseTexts.push(...submittedResponse.selectedOptions);
    if (submittedResponse.freeText) {
      responseTexts.push(submittedResponse.freeText);
    }
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "100",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "body-200",
      color: "text-core-subtle",
      children: questionText
    }), isPending ? /*#__PURE__*/_jsx(Small, {
      use: "help",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "breezeEngineChatComponents.askUserQuestion.awaitingResponse"
      })
    }) : /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "100",
      children: [/*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "breezeEngineChatComponents.askUserQuestion.responded"
        })
      }), responseTexts.map(text => /*#__PURE__*/_jsx(Text, {
        typography: "body-200",
        children: text
      }, text))]
    })]
  });
}